import React from "react";
import { Box, Paper, Typography, TextField, Button, Chip, Alert } from "@mui/material";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { PageContainer, SectionHeader, SelectorBar, SelectField, DataTable } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { market, savedBranch, saveBranch, canEdit, date, num, MarketLink, ConfirmAction } from "./community";

// Market → Book (service `market`, Docs/Market.md §2 / §10): the per-goods summary of a branch from `market stats <branch>` (open offers,
// units and the best price per side, the currency buys hold, the last 24 h of trades) and one goods' book - `market book <goods>
// <branch>`: both sides as price LEVELS (sells cheapest first, buys dearest first) + every open offer with its account (the operator's
// view; players never see accounts), cancel = `market cancel <offerId>` (closed and given back at once). The goods list = what the
// stats know (open or traded in 7 days) - the admin group does not list the title's tradeable goods, so any id can be typed.
// `#market` views, `market` cancels. /<game>/market/book/<branch>/<goods> opens a book.
export default class MarketBookTab extends React.Component {
    state = { branch: savedBranch(), stats: null, goods: "", typed: "", book: null, error: null, loading: false, busy: false, action: null };

    componentDidMount() {
        const deep = globals.route?.deep ?? [];
        const branch = globals.branches.includes(deep[0]) ? deep[0] : this.state.branch;
        const goods = globals.branches.includes(deep[0]) ? deep[1] ?? "" : deep[0] ?? "";
        this.setState({ branch, goods, typed: goods }, () => { this.loadStats(); if (goods) this.loadBook(goods); });
    }

    loadStats = () => {
        saveBranch(this.state.branch);
        this.setState({ loading: true });
        market("Loading the market stats", ["stats", this.state.branch], { silent: true })
            .then(r => this.setState({ stats: r && typeof r === "object" ? r : null }))
            .catch(e => this.setState({ stats: null, error: e?.message ?? "failed" }))
            .finally(() => this.setState({ loading: false }));
    }

    loadBook = (goods = this.state.goods) => {
        const g = String(goods ?? "").trim();
        if (!g) return;
        this.setState({ goods: g, typed: g, error: null });
        replaceDeep([this.state.branch, g]);
        market("Loading the book", ["book", g, this.state.branch], { silent: true })
            .then(r => this.setState({ book: r && typeof r === "object" ? r : null }))
            .catch(e => this.setState({ book: null, error: e?.message ?? "failed" }));
    }

    reload = () => { this.loadStats(); if (this.state.goods) this.loadBook(); }

    cancel = (o) => this.setState({ action: {
        title: `Cancel offer ${o.id}?`, confirmText: "Cancel the offer",
        text: `${o.kind} ${num(o.count)} × ${this.state.goods} at ${num(o.unitPrice)} of ${o.account}: closed now and given back (${o.kind === "buy" ? `${num(o.count * o.unitPrice)} currency` : `${num(o.count)} ${this.state.goods}`} return to the account's wallet).`,
        run: () => {
            this.setState({ busy: true });
            return market("Cancelling the offer", ["cancel", o.id], { successMessage: "Offer cancelled" })
                .then(() => { this.setState({ action: null }); this.reload(); })
                .catch(() => {})
                .finally(() => this.setState({ busy: false }));
        },
    }});

    // One row per goods the stats know: the open sides (best price = the cheapest sell / the dearest buy) and the last 24 h
    summaryRows() {
        const s = this.state.stats;
        if (!s) return [];
        const ids = [...new Set([...(s.open ?? []).map(o => o.goods), ...(s.last7d ?? []).map(t => t.goods)])].sort();
        return ids.map(goods => {
            const sell = (s.open ?? []).find(o => o.goods === goods && o.side === "sell"), buy = (s.open ?? []).find(o => o.goods === goods && o.side === "buy");
            const day = (s.last24h ?? []).find(t => t.goods === goods);
            return { goods, sell, buy, day };
        });
    }

    render() {
        const { branch, stats, goods, typed, book, error, loading, busy, action } = this.state;
        const edit = canEdit("market");
        const summary = this.summaryRows();
        const goodsLink = (g) => (
            <Typography component="span" variant="body2" sx={{ color: "primary.main", cursor: "pointer", fontFamily: "monospace", wordBreak: "break-all", fontWeight: g === goods ? 700 : 400 }}
                        onClick={() => this.loadBook(g)}>{g}</Typography>);
        const summaryColumns = [
            { id: "goods", label: "Goods", width: "14%", render: r => goodsLink(r.goods) },
            { id: "bestSell", label: "Best sell", width: "11%", align: "right", render: r => r.sell ? num(r.sell.best) : "—" },
            { id: "sellUnits", label: "Sell units", width: "10%", align: "right", render: r => num(r.sell?.units) },
            { id: "sellOffers", label: "Sell offers", width: "9%", align: "right", render: r => num(r.sell?.offers) },
            { id: "bestBuy", label: "Best buy", width: "11%", align: "right", render: r => r.buy ? num(r.buy.best) : "—" },
            { id: "buyUnits", label: "Buy units", width: "10%", align: "right", render: r => num(r.buy?.units) },
            { id: "buyOffers", label: "Buy offers", width: "9%", align: "right", render: r => num(r.buy?.offers) },
            { id: "held", label: "Currency held", width: "10%", align: "right", render: r => num(r.buy?.held) },
            { id: "trades", label: "Trades 24 h", width: "8%", align: "right", render: r => num(r.day?.trades) },
            { id: "volume", label: "Volume 24 h", width: "8%", align: "right", render: r => num(r.day?.volume) },
        ];
        const levelColumns = [
            { id: "price", label: "Price", width: "50%", align: "right", render: l => num(l.price) },
            { id: "units", label: "Units", width: "50%", align: "right", render: l => num(l.units) },
        ];
        const offerColumns = [
            { id: "kind", label: "Side", width: "7%", render: o => <Chip size="small" color={o.kind === "sell" ? "warning" : "info"} label={o.kind} /> },
            { id: "account", label: "Account", width: "14%", render: o => <MarketLink id={o.account} /> },
            { id: "count", label: "Count", width: "8%", align: "right", render: o => num(o.count) },
            { id: "posted", label: "Posted", width: "8%", align: "right", render: o => num(o.posted) },
            { id: "unitPrice", label: "Unit price", width: "10%", align: "right", render: o => num(o.unitPrice) },
            { id: "total", label: "Total", width: "10%", align: "right", render: o => num(o.count * o.unitPrice) },
            { id: "created", label: "Created", width: "13%", render: o => date(o.created) },
            { id: "expires", label: "Expires", width: "13%", render: o => date(o.expires) },
            { id: "cleanAt", label: "Clean check", width: "9%", render: o => <span title="the last clean-account check of its account (a Post / Take, or the sweep's clean pass)">{date(o.cleanAt)}</span> },
            { id: "actions", label: "", width: "8%", align: "right", render: o => edit ? <Button size="small" color="error" onClick={() => this.cancel(o)} disabled={busy}>Cancel</Button> : "" },
        ];
        const sells = book?.sells ?? [], buys = book?.buys ?? [];
        const spread = sells.length && buys.length ? sells[0].price - buys[0].price : null;
        return (
            <PageContainer>
                <SectionHeader title="Market book">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · {stats?.titleBranch ?? branch} · fee {stats?.feePct ?? "?"} % on the seller's side</Typography>
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches}
                                 onChange={v => this.setState({ branch: v, book: null }, () => { this.loadStats(); if (this.state.goods) this.loadBook(); })} />
                    <SelectField label="Goods" size="small" minWidth={180} value={summary.some(r => r.goods === goods) ? goods : ""} options={summary.map(r => r.goods)} onChange={v => this.loadBook(v)} />
                    <TextField label="Goods id" size="small" value={typed} onChange={e => this.setState({ typed: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.loadBook(typed); }} sx={{ width: 180 }} />
                    <Button variant="outlined" onClick={() => typed.trim() ? this.loadBook(typed) : this.loadStats()} disabled={loading}>Open</Button>
                    <Button onClick={this.reload} disabled={loading}>{loading ? "Loading…" : "Reload"}</Button>
                </SelectorBar>
                {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => this.setState({ error: null })}>{error}</Alert>}

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Goods</Typography>
                {summary.length > 0
                    ? <DataTable sx={FIXED_TABLE} columns={summaryColumns} rows={summary} getRowKey={r => r.goods} />
                    : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{loading || !stats ? "Loading…" : "No open offer and no trade in 7 days on this branch."}</Typography></Paper>}

                {book && (
                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            <span style={{ fontFamily: "monospace" }}>{book.goods}</span>
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                {book.titleBranch} · {sells.length} sell level(s), {buys.length} buy level(s){spread !== null ? ` · spread ${num(spread)}` : ""} · {(book.offers ?? []).length} open offer(s)
                            </Typography>
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Sells (cheapest first)</Typography>
                                {sells.length ? <DataTable sx={FIXED_TABLE} columns={levelColumns} rows={sells} getRowKey={l => `s${l.price}`} /> : <Typography variant="body2" color="text.secondary">None.</Typography>}
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Buys (dearest first)</Typography>
                                {buys.length ? <DataTable sx={FIXED_TABLE} columns={levelColumns} rows={buys} getRowKey={l => `b${l.price}`} /> : <Typography variant="body2" color="text.secondary">None.</Typography>}
                            </Box>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Open offers</Typography>
                        {(book.offers ?? []).length > 0
                            ? <DataTable sx={FIXED_TABLE} columns={offerColumns} rows={book.offers} getRowKey={o => o.id} />
                            : <Typography variant="body2" color="text.secondary">None open.</Typography>}
                    </Paper>
                )}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
